/**
 * Merchant Pro RBAC Sub-phase C3 — PermissionMatrixEditor.
 *
 * 46-capability × 1-role grid for editing a custom role's capability
 * set. System defaults are read-only (`disabled` prop forces all
 * checkboxes inactive). The base enum's system-default capability
 * set is shown as the "allowable" mask — toggles can only turn ON
 * within that mask.
 *
 * **Tier-1 invariant** — uses capabilities catalog ONLY · no
 * Tier-1 invariant — uses capabilities catalog ONLY · no role-equality
 * JSX · no hardcoded capability list (everything comes
 * from `/api/owner/staff/capabilities`).
 */
import { useMemo } from 'react';
import { Check, Lock } from 'lucide-react';

export default function PermissionMatrixEditor({
  catalog,
  baseRoleSystemDefaultCaps,
  selectedCapabilities,
  onChange,
  disabled = false,
}) {
  const baseMask = useMemo(
    () => new Set(baseRoleSystemDefaultCaps || []),
    [baseRoleSystemDefaultCaps],
  );
  const selected = useMemo(
    () => new Set(selectedCapabilities || []),
    [selectedCapabilities],
  );

  const toggleCap = (capId, on) => {
    if (disabled) return;
    if (!baseMask.has(capId)) return; // cannot escape the base mask
    const next = new Set(selected);
    if (on) next.add(capId);
    else next.delete(capId);
    onChange?.(Array.from(next));
  };

  if (!catalog || !catalog.groups) {
    return (
      <div
        className="text-sm text-white/55 py-8 text-center"
        data-testid="permission-matrix-loading"
      >
        Loading capability catalog…
      </div>
    );
  }

  return (
    <div
      data-testid="permission-matrix-editor"
      className="rounded-2xl border border-white/10 bg-[#15151B] overflow-hidden"
    >
      <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-white">Capability matrix</div>
          <div className="text-xs text-white/55 mt-0.5">
            {disabled ? (
              <span className="inline-flex items-center gap-1"><Lock size={12} /> Read-only (system default — clone to edit)</span>
            ) : (
              <>
                {selected.size} / {baseMask.size} capabilities enabled · faded ✓ = available in base role
              </>
            )}
          </div>
        </div>
        <div className="text-xs text-white/40">
          {catalog.total} total capabilities
        </div>
      </div>

      <div className="divide-y divide-white/5 max-h-[60vh] overflow-y-auto">
        {catalog.groups.map((g) => {
          // Count enabled-in-mask + selected within this domain for the header pill.
          const inMask = g.capabilities.filter((c) => baseMask.has(c.id)).length;
          const inSelected = g.capabilities.filter((c) => selected.has(c.id)).length;
          return (
            <div key={g.domain} data-testid={`matrix-domain-${g.domain.replace(/\s+/g, '-').toLowerCase()}`}>
              <div className="px-5 py-2 bg-[#0E0E14] text-xs font-semibold uppercase tracking-widest text-white/55 flex items-center justify-between">
                <span>{g.domain}</span>
                <span className="text-gold/80 font-mono">
                  {inSelected}/{inMask}{inMask < g.capabilities.length ? ` (${g.capabilities.length} total)` : ''}
                </span>
              </div>
              <ul>
                {g.capabilities.map((c) => {
                  const isInMask = baseMask.has(c.id);
                  const isSelected = selected.has(c.id);
                  return (
                    <li
                      key={c.id}
                      data-testid={`matrix-cap-${c.id}`}
                      className={
                        'px-5 py-2 flex items-start gap-3 border-t border-white/5 ' +
                        (!isInMask ? 'opacity-40' : '')
                      }
                    >
                      <label className="flex-1 cursor-pointer flex items-start gap-3 select-none">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={disabled || !isInMask}
                          onChange={(e) => toggleCap(c.id, e.target.checked)}
                          data-testid={`matrix-toggle-${c.id}`}
                          className="mt-1 w-4 h-4 rounded border-white/20 bg-[#0E0E14] text-gold focus:ring-gold/40"
                        />
                        <span className="flex-1">
                          <span className="block text-sm font-medium text-white/90 font-mono">
                            {c.id}
                          </span>
                          <span className="block text-xs text-white/55 mt-0.5">
                            {c.description}
                          </span>
                        </span>
                        {!isInMask && (
                          <span className="text-[10px] uppercase tracking-widest text-white/30 font-semibold mt-1.5">
                            n/a
                          </span>
                        )}
                        {isInMask && !isSelected && (
                          <span className="text-[10px] uppercase tracking-widest text-white/30 font-semibold mt-1.5 inline-flex items-center gap-0.5">
                            <Check size={10} /> base
                          </span>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
